import { Component, OnDestroy } from '@angular/core';
import { SocketService } from '../shared/services/socket.service';
import { CookieService } from 'ngx-cookie-service';
import { Router, ActivatedRoute } from '@angular/router';
import { GameComponent } from '../game/game.component';
import { Message } from '../shared/model/message';
import { MessageType } from '../shared/model/message-type';
import { DiceMessage, DiceMessageType } from '../shared/model/dice-message';
import { LiarsDiceGame, LiarsDiceGameState } from '../shared/model/liars-dice-game';
import { GameStatus } from '../shared/model/game-status';
import { WindowsService } from '../shared/services/windows.service';

@Component({
  selector: 'pbg-liars-dice',
  templateUrl: './liars-dice.component.html',
  styleUrls: ['./liars-dice.component.scss']
})
export class LiarsDiceComponent extends GameComponent implements OnDestroy {
  defaultDice = [1, 2, 3, 4, 5, 6];
  dice: number[] = [1, 2, 3, 4, 5];
  spinCount = 0;
  maxSpins = 10;
  spinInterval;
  startingScore = 5;
  canChallengeExact = false;

  turnUserName: string;
  hasSpun = false;
  hasSubmission = false;
  submitMode = false;
  canChallenge = false;

  diceGuess = {
    count: 1,
    die: 1
  };

  hostDice = {
    user: '',
    count: 0,
    die: 0
  };

  constructor(
    socketService: SocketService,
    cookieService: CookieService,
    router: Router,
    resolver: ActivatedRoute,
    public windowService: WindowsService
  ) {
    super(socketService, cookieService, router, resolver);
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.spinInterval) {
      this.windowService.clearInterval(this.spinInterval);
    }
  }

  messageHandler(message: Message) {
    if (message.action === MessageType.Prompt || message.action === MessageType.Host) {
      const gameState = <LiarsDiceGameState>message.content;

      if (Object.keys(gameState).length > 0) {
        this.setGameState(gameState);
      }
    }
  }

  setGameState(gameState: LiarsDiceGameState) {
    if (
      gameState.eliminated.indexOf(this.user.name) > -1 ||
      gameState.status === GameStatus.Complete
    ) {
      this.router.navigate(['/login']);
    } else if (gameState.dice[this.user.name]) {
      this.dice = gameState.dice[this.user.name];
      this.turnUserName = gameState.submitter;
      this.canChallengeExact = Object.keys(gameState.dice).length === 2;

      if (gameState.shouldRespin) {
        this.spin();
      }

      this.hasSubmission = Object.keys(gameState.currentSubmission).length > 0;
      this.canChallenge =
        this.user.name !== gameState.currentSubmission.user && this.hasSubmission;

      this.submitMode = this.user.name === gameState.submitter;

      this.hostDice = gameState.currentSubmission;
    }
  }

  spin() {
    this.spinInterval = this.windowService.setInterval(() => {
      this.spinHandler();
    }, 200);
  }

  spinHandler() {
    for (let i = 0; i < this.dice.length; i++) {
      this.dice[i] = this.getRandomDie();
    }

    this.spinCount++;

    if (this.spinCount >= this.maxSpins) {
      this.spinCount = 0;
      this.stopSpinning();
    }
  }

  sendDice() {
    const message: DiceMessage = {
      user: this.user.name,
      dice: this.dice,
      type: DiceMessageType.Dice
    };

    this.socketService.send({
      from: this.user,
      content: message,
      action: MessageType.Response,
      gameId: this.gameId
    });
  }

  sendGuess() {
    if (this.isDiceSubmissionValid()) {
      this.submitMode = false;
      this.canChallenge = false;

      const message: DiceMessage = {
        user: this.user.name,
        dice: this.dice,
        guess: this.diceGuess,
        type: DiceMessageType.Guess
      };

      this.socketService.send({
        from: this.user,
        content: message,
        action: MessageType.Response,
        gameId: this.gameId
      });

      this.diceGuess = {
        count: 1,
        die: 1
      };
    } else {
      this.windowService.alert(
        `Dice submission is not valid:\n\n` +
          `The quantity of dice can never be lowered, however if you raise the quantity, the dice face can be lowered.\n\n` +
          `Remember: 1\'s are wild and count in the dice total!`
      );
    }
  }

  sendChallenge(exacto = false) {
    this.submitMode = false;
    this.canChallenge = false;
    const message: DiceMessage = {
      user: this.user.name,
      dice: this.dice,
      guess: this.diceGuess,
      exacto: exacto,
      type: DiceMessageType.Challenge
    };

    this.socketService.send({
      action: MessageType.Response,
      content: message,
      from: this.user,
      gameId: this.gameId
    });
  }

  stopSpinning() {
    if (this.spinInterval) {
      this.windowService.clearInterval(this.spinInterval);
    }

    this.hasSpun = true;
    this.sendDice();
  }

  getRandomDie() {
    return Math.round(Math.random() * 5) + 1;
  }

  selectDie(die: number) {
    this.diceGuess.die = die;
  }

  isDiceSubmissionValid() {
    if (this.diceGuess.count === this.hostDice.count) {
      return this.diceGuess.die > this.hostDice.die;
    } else if (this.diceGuess.count < this.hostDice.count) {
      return false;
    }

    return true;
  }
}
